// Apolo IDE — BrainSearch (Sprint v2 Epic B6 WS B6.2 · brain-vivo AMB2/SYN1).
//
// Busca o vault no ÍNDICE LOCAL primeiro (~100ms, semântico, privado) com o
// MCP fuzzy como fallback quando o índice está vazio/indisponível. Resultado
// rico (título/path/heading/score/trecho) + ações: abrir no Obsidian e "usar
// no chat" (evento apolo:insertComposer existente — SessionComposer escuta).
// Botão "Sintetizar" roda a síntese LOCAL com citações (SYN1, opt-in).
// UMA região aria-live (announcer único).

import { useEffect, useMemo, useState } from 'react';
import { Loader2, MessageSquarePlus, Search, Sparkles } from 'lucide-react';
import { useBrainMCP } from '../../../../hooks/useBrainMCP.js';

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

export default function BrainSearch() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [local, setLocal] = useState({ tried: false, results: null, loading: false });
  const [synth, setSynth] = useState({ loading: false, data: null, error: null });
  const [announce, setAnnounce] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // índice local primeiro (apolo-brain-vivo AMB2)
  useEffect(() => {
    let alive = true;
    setSynth({ loading: false, data: null, error: null });
    if (!debounced) { setLocal({ tried: false, results: null, loading: false }); return undefined; }
    const corpus = typeof window !== 'undefined' ? window.apolo?.corpus : null;
    if (!corpus?.query) { setLocal({ tried: true, results: null, loading: false }); return undefined; }
    setLocal((s) => ({ ...s, loading: true }));
    corpus.query({ query: debounced, k: PAGE_SIZE })
      .then((res) => {
        if (!alive) return;
        const ok = !!res?.ok && Array.isArray(res.results) && res.results.length > 0;
        setLocal({ tried: true, results: ok ? res.results : null, loading: false });
        if (ok) setAnnounce(`${res.results.length} resultados do índice local`);
      })
      .catch(() => { if (alive) setLocal({ tried: true, results: null, loading: false }); });
    return () => { alive = false; };
  }, [debounced]);

  const useLocalResults = !!local.results;
  // MCP só como FALLBACK: índice já tentado e sem resultados
  const args = useMemo(() => ({ query: debounced, limit: PAGE_SIZE }), [debounced]);
  const { data, loading: mcpLoading, error } = useBrainMCP('obsidian_search', args, {
    enabled: !!debounced && local.tried && !useLocalResults,
  });

  const loading = local.loading || (!useLocalResults && mcpLoading);
  const results = useLocalResults
    ? local.results.map((r) => ({
        title: r.title || r.path?.split('/').pop() || 'Sem título',
        path: r.path,
        heading: r.heading && !String(r.heading).startsWith('∅') ? r.heading : '',
        score: r.score,
        summary: String(r.chunk || '').slice(0, 220),
        localHit: true,
      }))
    : (data?.results || data?.notes || []).map((r) => ({
        title: r.title || r.name || r.path?.split('/').pop() || 'Sem título',
        path: r.path || r.note?.path,
        heading: '',
        score: null,
        summary: r.summary || r.snippet || '',
        localHit: false,
      }));

  function openNote(path) {
    if (!path) return;
    if (typeof window !== 'undefined' && window.apolo?.openInObsidian) {
      window.apolo.openInObsidian(path).catch(() => {});
    }
  }

  function insertInChat(r) {
    const text = `Do meu vault — [[${r.path}]]${r.heading ? ` (${r.heading})` : ''}:\n> ${r.summary}\n`;
    window.dispatchEvent(new CustomEvent('apolo:insertComposer', { detail: { text } }));
    setAnnounce('Referência enviada pro composer');
  }

  async function runSynthesis() {
    const corpus = typeof window !== 'undefined' ? window.apolo?.corpus : null;
    if (!corpus?.synthesize || synth.loading) return;
    setSynth({ loading: true, data: null, error: null });
    try {
      const res = await corpus.synthesize({ query: debounced });
      if (res?.ok && res.synthesis) {
        setSynth({ loading: false, data: res, error: null });
        setAnnounce('Síntese pronta');
      } else {
        setSynth({ loading: false, data: null, error: res?.error || 'síntese indisponível' });
      }
    } catch (e) {
      setSynth({ loading: false, data: null, error: e?.message || 'síntese falhou' });
    }
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <div aria-live="polite" className="sr-only">{announce}</div>
      <label className="flex items-center gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 focus-within:border-apolo-accent">
        <Search size={12} className="text-apolo-textTertiary" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar no vault…"
          aria-label="Buscar no Pedro's Brain"
          className="flex-1 bg-transparent text-sm text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
        />
        {loading && <Loader2 size={12} className="animate-spin text-apolo-textTertiary" aria-hidden="true" />}
      </label>

      {useLocalResults && (
        <div className="flex items-center justify-between">
          <span className="rounded-full border border-apolo-borderSubtle px-2 py-0.5 text-[10px] text-apolo-textTertiary">índice local · semântico</span>
          {window.apolo?.corpus?.synthesize && results.length >= 2 && (
            <button
              type="button"
              onClick={runSynthesis}
              disabled={synth.loading}
              aria-busy={synth.loading}
              className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-0.5 text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface3 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50 disabled:opacity-50"
            >
              <Sparkles size={10} aria-hidden="true" className={synth.loading ? 'animate-pulse' : ''} />
              {synth.loading ? 'sintetizando localmente…' : 'Sintetizar'}
            </button>
          )}
        </div>
      )}

      {synth.data && (
        <div className="rounded-md border border-apolo-borderSubtle bg-apolo-surface1 p-2 text-xs text-apolo-textPrimary">
          <p className="whitespace-pre-wrap">{synth.data.synthesis}</p>
          {Array.isArray(synth.data.citations) && synth.data.citations.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {synth.data.citations.map((c, i) => (
                <button key={`${c.path}-${i}`} type="button" onClick={() => openNote(c.path)}
                  className="rounded-full border border-apolo-borderSubtle px-2 py-0.5 text-[10px] text-apolo-textSecondary transition hover:bg-apolo-surface3 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50">
                  {c.path.split('/').pop()}
                </button>
              ))}
            </div>
          )}
          {synth.data.gap && (
            <p className="mt-1.5 text-[11px] text-apolo-textTertiary">Lacunas: {synth.data.gap}</p>
          )}
        </div>
      )}
      {synth.error && (
        <p className="text-[11px] text-apolo-textTertiary">Síntese: {synth.error}</p>
      )}

      {error && !useLocalResults && (
        <div role="alert" className="rounded-md border border-apolo-error/30 bg-apolo-error/10 px-2 py-1 text-xs text-apolo-error">
          {error}
        </div>
      )}
      {!debounced && (
        <p className="text-xs text-apolo-textTertiary">Digite para buscar no vault (semântico, local).</p>
      )}
      {debounced && !loading && results.length === 0 && !error && (
        <p className="text-xs text-apolo-textTertiary">Sem resultados para “{debounced}”.</p>
      )}
      <ul className="flex flex-col gap-1">
        {results.map((r, i) => (
          <li key={`${r.path || i}-${i}`} className="rounded-md border border-apolo-borderSubtle bg-apolo-surface1">
            <button
              type="button"
              onClick={() => openNote(r.path)}
              className="flex w-full flex-col gap-0.5 rounded-t-md px-2 py-1.5 text-left transition hover:bg-apolo-surface2 focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
              aria-label={`Abrir nota ${r.title} no Obsidian`}
            >
              <span className="flex items-baseline gap-2">
                <span className="truncate text-sm text-apolo-textPrimary">{r.title}</span>
                {r.heading && <span className="truncate text-[10px] text-apolo-textTertiary">{r.heading}</span>}
                {typeof r.score === 'number' && <span className="ml-auto shrink-0 text-[10px] text-apolo-textTertiary">{r.score.toFixed(2)}</span>}
              </span>
              {r.summary && <span className="line-clamp-2 text-xs text-apolo-textTertiary">{r.summary}</span>}
            </button>
            {r.localHit && (
              <div className="flex border-t border-apolo-borderSubtle/60">
                <button
                  type="button"
                  onClick={() => insertInChat(r)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] text-apolo-textTertiary transition hover:text-apolo-textSecondary focus:outline-none focus:ring-2 focus:ring-apolo-accent/50"
                  aria-label={`Usar trecho de ${r.title} no chat`}
                >
                  <MessageSquarePlus size={10} aria-hidden="true" />
                  usar no chat
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
